import React, { useState } from 'react';

// Utils
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import useNotification from '../../utils/hooks/useNotification';
import withAuth from '../../utils/hocs/withAuth';
import withPermission from '../../utils/hocs/withPermission';
import SCOPE from '../../utils/gate/scope';
import withSidebar from '../../utils/hocs/withSidebar';
import packageSchema from '../../validations/packageSchema';
import useForm from '../../utils/hooks/useForm';

// Services
import usePackageService from '../../services/packageService';
import useAccommodationService from '../../services/accommodationService';
import useCampService from '../../services/campService';
import useExtraService from '../../services/extraService';

// Components
import PageWrapper from '../../components/layout/PageWrapper/PageWrapper';
import PackageConfiguration from '../../components/views/establishment/Packages/PackageConfiguration';
import Button from '../../components/atoms/Button/Button';

function PackageCreation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [extrasWithQuantities, setExtrasWithQuantities] = useState([]);
  const [extrasTouched, setExtrasTouched] = useState(false);

  const [initialValues] = useState({
    name: '',
    isActive: true,
    campId: null,
    accommodationIds: [],
    isAccommodationMandatory: false,
  });

  const { postPackage } = usePackageService();
  const { getAllCamps } = useCampService();
  const { getAllExtras } = useExtraService();
  const { getAllAccommodations } = useAccommodationService();

  const { isLoading: isPostPackageLoading, mutate: postPackageMutate } = useMutation(
    (values) => postPackage(values),
    {
      onSuccess: () => {
        navigate('/packages', {
          state: 'packages.creation.success',
        });
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'packages.creation.error'),
          color: 'error',
        });
      },
    },
  );

  const { data: camps, isFetching: isFetchingCamps } = useQuery('getAllCamps', () => getAllCamps());
  const { isFetching: isFetchingExtras } = useQuery(
    'getAllExtras',
    () => getAllExtras(),
    {
      onSuccess: (data) => {
        if (data?.data?.extras?.length) {
          setExtrasWithQuantities(data.data.extras.map((extra) => ({
            id: extra.id,
            label: extra.name,
            quantity: 0,
          })));
        }
      },
    },
  );
  const { data: accommodations, isFetching: isFetchingAccommodations } = useQuery(
    'getAllAccommodations',
    () => getAllAccommodations(),
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        postPackageMutate({
          name: values.name,
          isActive: values.isActive,
          campId: values.campId?.value,
          extras: extrasWithQuantities
            ?.filter((extra) => extra.quantity > 0)
            ?.map((extra) => ({ extraId: extra.id, maxQuantity: extra.quantity })) || [],
          accommodationIds: values.accommodationIds?.map((acc) => acc.value),
          isAccommodationMandatory: values.isAccommodationMandatory,
        });
      }
    },
    validationSchema: packageSchema,
    validateOnBlur: true,
  });

  return (
    <PageWrapper
      title={t('packages.creation.title')}
      hasWhiteWrapper
      breadcrumb={[
        { label: t('packages.title'), href: '/packages' },
      ]}
    >
      <PackageConfiguration
        formik={{
          values: formik.values,
          errors: formik.errors,
          touched: formik.touched,
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
          setFieldValue: formik.setFieldValue,
          setFieldTouched: formik.setFieldTouched,
        }}
        isDataLoading={isFetchingCamps || isFetchingAccommodations || isFetchingExtras}
        camps={camps?.data?.camps || []}
        accommodations={accommodations?.data?.accommodations || []}
        extrasWithQuantities={extrasWithQuantities}
        setExtrasWithQuantities={setExtrasWithQuantities}
        setExtrasTouched={setExtrasTouched}
      />

      <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
        <Button
          full={isMobile}
          onClick={() => {
            formik.submitForm().then(() => handleScrollToError(formik.errors));
          }}
          isLoading={isPostPackageLoading}
          disabled={!formik.dirty && !extrasTouched}
        >
          {t('packages.creation.submit')}
        </Button>
      </div>
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(PackageCreation),
    {
      scopes: [SCOPE.create_ownEst_package, SCOPE.update_ownEst_package],
    },
  ),
);
